import { getPlanFeatureSettingsPlanFeatureSettingsGetOptions } from '@/generated/@tanstack/react-query.gen'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { z } from 'zod'

const abilityCheckSchema = z.object({
  search_by_criteria: z.boolean(), // Search by Criteria
  gsa_ratings: z.boolean(), // GSA Ratings
  water_district_ratings: z.boolean(), // Water District Ratings
  bulk_upload: z.boolean(), // Bulk Upload
  report_generation: z.boolean(), // Report Generation
  subbasin_executive_summaries: z.boolean(), // Subbasin Executive Summaries
  gsa_executive_summaries: z.boolean(), // GSA Executive Summaries
  hydra_score: z.boolean(), // Hydra Score
  soil_score: z.boolean(), // Soil Score
  apn_search: z.boolean(), // APN Search
  custom_ranches_portfolio: z.boolean(), // Custom Ranches / Portfolio
  ewrims_diversion_points: z.boolean(), // eWRIMS Diversion Points
  apn_display: z.boolean(), // APN Display
  ownership_information: z.boolean(), // Ownership Information
  gsa_and_water_district_boundaries: z.boolean(), // GSA and Water District Boundaries
  historical_climate_data: z.boolean(), // Historical Climate Data
  statewide_crops: z.boolean(), // Statewide crops
  assembly_and_senate_district: z.boolean(), // Assembly and Senate District
  hydrologic_region: z.boolean(), // Hydrologic Region
  subbasin_information_name_number: z.boolean(), // Subbasin Information (name, number)
  sagbi_modified_and_unmodified: z.boolean(), // SAGBI (modified and unmodified)
  soil_properties: z.boolean(), // Soil Properties
  soil_types: z.boolean(), // Soil Types
  flood_maps: z.boolean(), // Flood Maps
  property_lines: z.boolean(), // Property Lines
})

const abilityLimitSchema = z.object({
  number_of_reports: z.number(), // # of Reports / Month
  number_of_custom_ranches: z.number(), // Number of Custom Ranches
  number_of_remaining_ranches: z.number().nullish(),
  number_of_remaining_reports: z.number().nullish(),
  number_of_remaining_criteria_search: z.number().nullish(),
})

export type TAbilityCheck = z.infer<typeof abilityCheckSchema>
export type TAbilityLimit = z.infer<typeof abilityLimitSchema>

const planFeatureSettingsSchema = z.object({
  plan: z.string().nullish(),
  settings: abilityCheckSchema.merge(abilityLimitSchema),
})

export type AbilityReportType = {
  hydraScore: boolean
  apnDisplay: boolean
  soilProperties: boolean
  soilTypes: boolean
  soilScore: boolean
  gsaRatings: boolean
  wdRatings: boolean
  gsaAndWaterDistrictBoundaries: boolean
  hydrologicRegion: boolean
  subbasinExecutiveSumary: boolean
  gsaExecutiveSumary: boolean
  subbasinInformation: boolean
  assemblyAndSenateDistricts: boolean
  floodMaps: boolean
  historicalClimateData: boolean
  ownershipInformation: boolean
}

/* HOW TO USE
import useAbility from '@/hooks/use-ability'
To make sure this hook has access to the data, you need to wrap the component with the QueryClientProvider
Need check isLoading to make sure the data is loaded - In the next second call, data will always be available
*/

const useAbility = () => {
  const { data, isLoading, isError, refetch } = useQuery({
    ...getPlanFeatureSettingsPlanFeatureSettingsGetOptions({}),
    select: (data) => {
      const parsed = planFeatureSettingsSchema.safeParse(data)
      if (!parsed.success) {
        console.error('Schema validation failed', parsed.error)
        return null
      }
      return parsed.data.settings
    },
    staleTime: 5 * 60 * 1000,
    retryOnMount: false,
  })

  const normalizedData = useMemo(() => {
    if (isLoading || !data) return null
    return data
  }, [isLoading, data])

  const checkAbility = useCallback(
    (ability: keyof TAbilityCheck): boolean => {
      if (!normalizedData) return false
      return normalizedData[ability]
    },
    [normalizedData]
  )

  const getAbilityLimit = useCallback(
    (ability: keyof TAbilityLimit): number => {
      if (!normalizedData) return 0
      return normalizedData[ability] || 0
    },
    [normalizedData]
  )

  const getAbilityOptionnalLimit = useCallback(
    (ability: keyof TAbilityLimit): number | undefined => {
      if (!normalizedData) return undefined
      return normalizedData[ability] ?? undefined
    },
    [normalizedData]
  )
  // console.log(normalizedData)

  const getAbilityReport = (): AbilityReportType => {
    return {
      // For testing
      // hydraScore: false,
      // soilScore: false,
      // gsaRatings: true,
      // gsaAndWaterDistrictBoundaries: false,
      // hydrologicRegion: true,
      // subbasinInformation: true,
      // subbasinExecutiveSumary: true,
      // assemblyAndSenateDistricts: true,
      // apnDisplay: true,
      // soilProperties: true,
      // soilTypes: true,
      // floodMaps: true,
      // historicalClimateData: true,
      // gsaExecutiveSumary: true,
      // ownershipInformation: true,

      ownershipInformation: checkAbility('ownership_information'),
      hydraScore: checkAbility('hydra_score'),
      soilScore: checkAbility('soil_score'),
      gsaAndWaterDistrictBoundaries: checkAbility(
        'gsa_and_water_district_boundaries'
      ),
      hydrologicRegion: checkAbility('hydrologic_region'),
      subbasinInformation: checkAbility('subbasin_information_name_number'),
      assemblyAndSenateDistricts: checkAbility('assembly_and_senate_district'),
      apnDisplay: checkAbility('apn_display'),
      soilProperties: checkAbility('soil_properties'),
      soilTypes: checkAbility('soil_types'),
      floodMaps: checkAbility('flood_maps'),
      historicalClimateData: checkAbility('historical_climate_data'),
      subbasinExecutiveSumary: checkAbility('subbasin_executive_summaries'),
      gsaExecutiveSumary: checkAbility('gsa_executive_summaries'),
      gsaRatings: checkAbility('gsa_ratings'),
      wdRatings: checkAbility('water_district_ratings'),
    }
  }

  const invalidateQuery = () => refetch()

  return {
    isLoading,
    isError,
    checkAbility,
    getAbilityLimit,
    getAbilityReport,
    getAbilityOptionalLimit: getAbilityOptionnalLimit,
    invalidateQuery,
  }
}

export default useAbility
